import React, { useState, useRef, useEffect } from "react";
import PostPDF from "../../services/PostPdf.js";
import CircularProgress from '@mui/material/CircularProgress';
import { Dialog, Autocomplete, TextField, Typography } from "@mui/material"
import SnackbarEx from "../Helpers/SnackbarEx.jsx";
import SpreadsheetResponse from "./SpreadsheetResponse.jsx";
import useApiData from "../../services/useApiData.js";

export default function SpreadsheetTab() {
    const { data: configs = [] } = useApiData('api/broylesllc/configuration/import-configs');
    const [pdfFile, setPdfFile] = useState(null)
    const [load, setLoad] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const [openResponseDialog, setOpenResponseDialog] = useState(false)
    const [responseData, setResponseData] = useState(null)
    const [invoiceId, setInvoiceId] = useState('')
    const [selectedConfig, setSelectedConfig] = useState(null)

    const [openTestSnack, setOpenTestSnack] = useState({
        color: "",
        message: "",
        open: false
    })

    useEffect(() => {
        if (!configs?.length) return;
        if (configs.length === 1) {
            setSelectedConfig(configs[0]);
            return;
        }
        const amazon = configs.find((c) =>
            /amazon/i.test(c.sourceName)
            || /amazon/i.test(c.supplierDisplayName || '')
            || /amazon/i.test(c.supplierNameStatic || '')
        );
        if (amazon && !selectedConfig) setSelectedConfig(amazon);
    }, [configs]);

    const handleFile = ({ target }) => {
        setPdfFile(target.files[0])
    }

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles.length > 0) {
            setPdfFile(Array.from(droppedFiles)[0])
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const fileInputRef = useRef(null);
    const handleBrowseClick = () => {
        fileInputRef.current.click();
    };

    const handleUpload = async (e) => {
        e.preventDefault()
        if (!selectedConfig) {
            setOpenTestSnack({ color: "warning", message: "Select an import configuration first.", open: true })
            return
        }
        if (!pdfFile) {
            setOpenTestSnack({ color: "warning", message: "Select a CSV file.", open: true })
            return
        }
        setLoad(true)

        try {
            const formData = new FormData()
            formData.append("file", pdfFile, pdfFile.name)
            formData.append("invoiceId", invoiceId)
            formData.append("configId", selectedConfig._id)

            const { data: PDFSuccess, error: PDFError } = await PostPDF(formData, "broylesllc/post-csv-invoice")
            if (PDFError || !PDFSuccess) {
                setOpenTestSnack({ color: "error", message: "CSV parse failed.", open: true })
                return
            }
            setResponseData(PDFSuccess)
            setOpenResponseDialog(true)
        } catch (err) {
            console.log(err)
            setOpenTestSnack({ color: "error", message: "CSV parse failed.", open: true })
        } finally {
            setLoad(false)
        }
    }

    const handleClose = (val) => {
        setOpenResponseDialog(false)
        if (val === "Success") {
            setOpenTestSnack({ color: "success", message: "Invoice added successfully! Check Catalog to see data.", open: true })
            setPdfFile(null)
            setInvoiceId('')
        } else if (val === "Exists") {
            setOpenTestSnack({ color: "warning", message: "Invoice already exists in Bread Bskt.", open: true })
        }
    }

    const configOptions = (configs || []).map((c) => ({
        ...c,
        label: c.supplierDisplayName || c.sourceName,
    }));

    return (
        <div style={{ padding: '0 1%', display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '3rem' }}>
            <div className='row m-0' style={{ width: '350px', marginBottom: '12px' }}>
                <Typography variant="body2" fontWeight={600} display="block" mb={0.5}>
                    1. Supplier
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Which CSV mapping to use (from Configuration → Supplier Presets).
                </Typography>
                <Autocomplete
                    options={configOptions}
                    getOptionLabel={(o) => o.label || o.sourceName || ''}
                    value={selectedConfig}
                    onChange={(_, v) => setSelectedConfig(v)}
                    isOptionEqualToValue={(a, b) => a?._id === b?._id}
                    size="small"
                    fullWidth
                    renderInput={(params) => (
                        <TextField {...params} placeholder="Select supplier…" />
                    )}
                />
            </div>
            <div className='row m-0' style={{ width: '350px', marginBottom: '4px' }}>
                <Typography variant="body2" fontWeight={600} display="block" mb={0.5}>
                    2. Invoice ID (optional)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Only if this preset expects you to type it (not from a CSV column).
                </Typography>
                <TextField
                    size="small"
                    name='invoice_id'
                    value={invoiceId}
                    onChange={(e) => setInvoiceId(e.target.value)}
                    sx={{ width: '100%' }}
                    placeholder="Leave blank if mapped from CSV"
                />
            </div>
            <Typography variant="body2" fontWeight={600} sx={{ width: '350px', mt: 1 }} display="block">
                3. CSV file
            </Typography>
            <div className='row' style={{
                marginTop: '10px',
                transition: 'border-color 0.2s, background-color 0.2s',
                borderStyle: 'dashed', width: '350px'
            }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <img
                    src="uploadIcon.svg"
                    alt="Upload Icon"
                    className="d-block mx-auto mb-3 text-secondary"
                    style={{ width: '4rem', height: '4rem' }}
                />
                <p>
                    <span>Drop your files here,</span> or{' '}
                    <button
                        onClick={handleBrowseClick}
                        className="btn btn-link p-0 fw-medium"
                    >
                        click to browse
                    </button>
                </p>
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFile}
                    accept=".csv,text/csv"
                    style={{ display: 'none' }}
                />
            </div>

            {pdfFile &&
                <div className='row' style={{ marginTop: '15px' }}>
                    <p style={{ margin: '0 0 0 10px' }}>File:<em> {pdfFile.name}</em></p>
                </div>
            }

            <div style={{ marginTop: '30px' }}>
                <button className='submit'
                    style={{ width: "325px", margin: '0', cursor: "pointer", display: 'flex', justifyContent: 'center', alignItems: 'center ' }}
                    disabled={!pdfFile || !selectedConfig || load}
                    onClick={handleUpload}>{load ? <CircularProgress size={20} sx={{ color: 'white' }} /> : "Upload File"}</button>
            </div>
            <Dialog maxWidth={'sm'} fullWidth open={openResponseDialog} onClose={(val) => handleClose(val)}>
                <SpreadsheetResponse responseData={responseData} closeDialog={(val) => handleClose(val)} />
            </Dialog>

            {openTestSnack.open && <SnackbarEx color={openTestSnack.color} message={openTestSnack.message} open={openTestSnack.open}
                close={() => setOpenTestSnack({ color: "", message: "", open: false })} />}
        </div>
    )
}
